import './PictureInput.css';

import { ChangeEvent, useRef } from 'react';
import toast from 'react-hot-toast';
import Button from '../../../../Button/Button';
import { useTranslate } from '../../../../../hooks/context/useTranslate';
import { getLayoutImageDataUrl } from '../../../../../utils/layouts/getLayoutImageDataUrl';
import { PICTURE_INPUT_ACCEPTED_TYPES } from '../../../../../constants';
import type { GridInputBaseProps } from '../../types';

type PictureInputProps = Pick<
  GridInputBaseProps<string | null>,
  'onChange' | 'onTouch' | 'disabled' | 'name'
> & {
  // Bare base64 bitmap (the API transports images without a data: prefix).
  value: string | null;
};

// File picker for a base64 bitmap (control type 'picture' / item type 'binary'):
// preview thumbnail + choose/remove buttons. null value = the bitmap is removed
// (the write semantics of the removal belong to the consumer).
const PictureInput = ({
  value,
  name,
  disabled,
  onChange,
  onTouch,
}: PictureInputProps) => {
  const { t } = useTranslate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    // Selecting the same file again must fire onChange too.
    event.target.value = '';

    if (!file) {
      return;
    }

    // The accept attribute is only a picker hint — the user can still pick
    // any file (e.g. via "All files"), so the type gets checked here too.
    if (!PICTURE_INPUT_ACCEPTED_TYPES.includes(file.type)) {
      toast(t('toasts.unsupportedImageFormat'));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      // The API stores bare base64 — strip the data URL prefix.
      const base64 = result.slice(result.indexOf(',') + 1);
      onChange(base64);
      onTouch?.(base64);
    };
    reader.readAsDataURL(file);
  };

  const handleRemove = (): void => {
    onChange(null);
    onTouch?.(null);
  };

  return (
    // .input-container matches the vertical padding of the standard inputs, so
    // the buttons row lines up with a string input in the same row.
    <div className="form-input picture-input input-container">
      {value && (
        <img
          className="picture-input-preview"
          src={getLayoutImageDataUrl(value)}
          alt={name}
        />
      )}
      <div className="picture-input-buttons">
        <input
          ref={fileInputRef}
          className="picture-input-file"
          type="file"
          accept={PICTURE_INPUT_ACCEPTED_TYPES.join(',')}
          disabled={disabled}
          onChange={handleFileChange}
        />
        {/* Default (non-small) buttons share the 40px min-height of the
            standard inputs (Button.css / Input.css). */}
        <Button
          disabled={disabled}
          onClick={(): void => fileInputRef.current?.click()}
        >
          {t('pictureInput.select')}
        </Button>
        {value && (
          <Button fill="outline" disabled={disabled} onClick={handleRemove}>
            {t('pictureInput.remove')}
          </Button>
        )}
      </div>
    </div>
  );
};

export default PictureInput;
